/**
 * Service to manage folder selection and ensure it's sent with all API requests
 */

const FOLDER_STORAGE_KEY = 'selected-s3-folder'; // Used for both S3 mode ('books'/'practice') and local mode (full directory path)
const FOLDER_CHANGE_EVENT = 'folderChanged';

export class FolderService {
  private static currentFolder: string | null = null;
  private static listeners: Set<(folder: string) => void> = new Set();

  /**
   * Initialize folder from URL parameter only
   * URL parameter is the single source of truth - BookFileSelector handles adding it if missing
   */
  static init(): void {
    // Only read from URL parameter - don't fall back to localStorage
    // BookFileSelector will ensure URL parameter exists before we need the folder
    const urlParams = new URLSearchParams(window.location.search);
    const booksToEdit = urlParams.get('books-to-edit');
    
    if (booksToEdit) {
      // Map URL parameter to folder value (only for S3 mode)
      // 'real' -> 'books', 'practice' -> 'practice'
      const folder = booksToEdit === 'real' ? 'books' : booksToEdit === 'practice' ? 'practice' : null;
      
      if (folder) {
        this.currentFolder = folder;
        localStorage.setItem(FOLDER_STORAGE_KEY, folder);
        console.log(`[FolderService] Initialized from URL parameter: books-to-edit=${booksToEdit} -> folder=${folder}`);
      }
    }
    // Don't fall back to localStorage - wait for URL parameter to be set by BookFileSelector
  }

  /**
   * If folder is not set, try to set it from the current URL (books-to-edit=real|practice).
   * Use when the app may have loaded before the URL had the param (e.g. redirect) or init ran without it.
   */
  static ensureFolderFromUrlIfUnset(): void {
    if (this.currentFolder) return;
    const urlParams = new URLSearchParams(window.location.search);
    const booksToEdit = urlParams.get('books-to-edit');
    if (booksToEdit === 'real' || booksToEdit === 'practice') {
      const folder = booksToEdit === 'real' ? 'books' : 'practice';
      this.setFolder(folder);
      console.log('[FolderService] Set folder from URL: books-to-edit=%s -> folder=%s', booksToEdit, folder);
    }
  }

  /**
   * Get current folder
   * Only returns folder if it was set from URL parameter
   * Throws error if folder is not set - NO FALLBACKS
   */
  static getFolder(): string {
    if (!this.currentFolder) {
      throw new Error('Folder is not set. Please select a folder first.');
    }
    return this.currentFolder;
  }

  /**
   * Get current folder without throwing (returns null if not set).
   */
  static getFolderIfSet(): string | null {
    return this.currentFolder;
  }

  /**
   * Set folder and clear all caches
   */
  static setFolder(folder: string): void {
    if (folder === this.currentFolder) {
      return; // No change
    }

    this.currentFolder = folder;
    localStorage.setItem(FOLDER_STORAGE_KEY, folder);

    // Clear all caches
    this.clearAllCaches();

    // Notify listeners
    this.notifyListeners(folder);

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent(FOLDER_CHANGE_EVENT, { detail: { folder } }));
  }

  /**
   * Clear all caches when folder changes
   * IMPORTANT: Never clears auth tokens (book-admin-auth-token, book-admin-user-name)
   */
  private static clearAllCaches(): void {
    // Import logger
    import('../utils/PersistentLogger').then(({ persistentLogger }) => {
      // Get all keys before clearing
      const allKeysBefore = Object.keys(localStorage);
      const authKeysBefore = allKeysBefore.filter(k => k.includes('auth') || k.includes('book-admin'));
      
      persistentLogger.log('FolderService', 'clearAllCaches() called', {
        totalKeys: allKeysBefore.length,
        authKeys: authKeysBefore
      });
      
      // PROTECTED KEYS - never remove these
      const PROTECTED_KEYS = [
        'book-admin-auth-token',
        'book-admin-user-name',
        'selected-s3-folder', // This is set by FolderService itself
        'app-logs' // Preserve logs
      ];
      
      // Clear localStorage caches related to books
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && !PROTECTED_KEYS.includes(key) && (
          key.startsWith('bookList') ||
          key.startsWith('current-book-list-id') ||
          key.startsWith('book-') ||
          key.startsWith('spine-') ||
          key.startsWith('page-') ||
          key.startsWith('word-data-')
        )) {
          keysToRemove.push(key);
        }
      }
      
      persistentLogger.log('FolderService', 'Keys to remove', { 
        keysToRemove,
        protectedKeys: PROTECTED_KEYS
      });
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage
      sessionStorage.clear();

      // Verify auth keys are still there
      const allKeysAfter = Object.keys(localStorage);
      const authKeysAfter = allKeysAfter.filter(k => k.includes('auth') || k.includes('book-admin'));
      
      const authTokenStillExists = localStorage.getItem('book-admin-auth-token') !== null;
      
      persistentLogger.log('FolderService', 'clearAllCaches() completed', {
        totalKeysBefore: allKeysBefore.length,
        totalKeysAfter: allKeysAfter.length,
        authKeysBefore,
        authKeysAfter,
        authTokenPreserved: authTokenStillExists,
        authTokenStillExists
      });
      
      if (!authTokenStillExists) {
        persistentLogger.error('FolderService', 'CRITICAL: Auth token was cleared! This should never happen!');
      }

      console.log('[FolderService] Cleared all book-related caches');
    });
  }

  /**
   * Add listener for folder changes
   */
  static addListener(listener: (folder: string) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify all listeners
   */
  private static notifyListeners(folder: string): void {
    this.listeners.forEach(listener => {
      try {
        listener(folder);
      } catch (error) {
        console.error('[FolderService] Error in listener:', error);
      }
    });
  }

  /**
   * Add folder to URL as query parameter
   */
  static addFolderToUrl(url: string): string {
    const folder = this.getFolder();
    if (!folder) {
      throw new Error('Folder is required but not set. Please select a folder first.');
    }

    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}folder=${encodeURIComponent(folder)}`;
  }

  /**
   * Get folder header for fetch requests
   */
  static getFolderHeader(): Record<string, string> {
    const folder = this.getFolder();
    if (!folder) {
      throw new Error('Folder is required but not set. Please select a folder first.');
    }
    return { 'X-Folder': folder };
  }

  /**
   * Get page JSON URL with folder parameter
   * This is the standard way to construct page JSON API URLs
   */
  static getPageJsonUrl(isbn: string, pageFilename: string): string {
    return this.addFolderToUrl(`/api/books/${isbn}/pages/${pageFilename}`);
  }
}

// Initialization is now handled by AppInitializer
// Do not call init() at module load


